import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
} from "@mui/material";
import { ReactNode, useEffect, useRef } from "react";

import { SafeDialog } from "./Dialog";

export type ResourceInfo = {
  resourceType: string | null | undefined;
  resourceName: string | number | null | undefined;
};

export type ResourceInfoDisplayProps = {
  resourceType: string | null | undefined;
  resourceName: string | number | null | undefined;
  labelSize?: number;
  valueSize?: number;
};

/**
 * Displays a single resource with label/value in a grid format.
 * Exported for use in custom ConfirmDialog content compositions.
 */
export function ResourceInfoDisplay({
  resourceType,
  resourceName,
  labelSize = 2.5,
  valueSize = 9.5,
}: ResourceInfoDisplayProps) {
  return resourceType || resourceName ? (
    <>
      <Grid size={labelSize}>
        <DialogContentText
          sx={{
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "text.secondary",
          }}
        >
          {resourceType}
        </DialogContentText>
      </Grid>
      <Grid size={valueSize}>
        <DialogContentText
          sx={{
            fontSize: "0.875rem",
            fontWeight: 400,
          }}
        >
          {resourceName}
        </DialogContentText>
      </Grid>
    </>
  ) : null;
}

/**
 * Container component for displaying a list of resources.
 * Exported for use in custom ConfirmDialog content compositions.
 */
export function ResourceList({
  resources,
  spacing = 2,
}: {
  resources: ResourceInfo[];
  spacing?: number;
}) {
  return (
    <Grid container spacing={spacing} paddingBottom={3} alignItems="center">
      {resources.map((resource, index) => (
        // Indexed: several same-typed rows would collide on the type alone. Static
        // text, so there's no state for an index key to strand.
        <ResourceInfoDisplay
          key={`${resource.resourceType}-${index}`}
          resourceType={resource.resourceType}
          resourceName={resource.resourceName}
        />
      ))}
    </Grid>
  );
}

type ConfirmDialogProps = {
  open: boolean;
  /** When provided, enables a double-click guard that blocks repeated clicks
   * before React re-renders with the new loading state. */
  loading?: boolean;
  /** When `loading` is provided, this callback should return its async work
   * (e.g. `return deleteMutation({...})`) so the double-click guard stays
   * active until the promise settles. */
  onConfirm?: () => void | Promise<unknown>;
  onCancel?: () => void;
  onClose: () => void;
  header: string;
  resources?: ResourceInfo[];
  preResourcesInfo?: string | ReactNode;
  info?: string | ReactNode;
  confirmText?: string | ReactNode;
  cancelText?: string | ReactNode;
  actions?: ReactNode;
  disableConfirm?: boolean;
  children?: ReactNode;
  confirmStartIcon?: ReactNode;
  minWidth?: string;
  maxWidth?: string;
};

export function ConfirmDialog(props: ConfirmDialogProps) {
  // Double-click guard: blocks repeated clicks before React re-renders with
  // loading=true. Only active when the caller passes the `loading` prop —
  // callers without it aren't performing async mutations so double-click
  // protection isn't needed, and we'd have no signal to reset the ref.
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!props.open) inFlightRef.current = false;
  }, [props.open]);

  const hasLoadingProp = props.loading !== undefined;

  const handleConfirm = async () => {
    if (hasLoadingProp && (inFlightRef.current || props.loading)) return;
    inFlightRef.current = true;
    try {
      await props.onConfirm?.();
    } finally {
      inFlightRef.current = false;
    }
  };

  const handleCancel = props.onCancel ?? props.onClose;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleConfirm();
    }
  };

  return (
    <SafeDialog
      open={props.open}
      onKeyDown={handleKeyDown}
      onClose={props.onClose}
      slotProps={{
        paper: {
          sx: {
            minWidth: props.minWidth ?? "440px",
            maxWidth: props.maxWidth ?? "640px",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {props.header}
        <IconButton
          aria-label="close"
          onClick={props.onClose}
          disabled={props.loading}
          sx={{
            alignSelf: "start",
            marginLeft: 2,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {props.children ? (
          props.children
        ) : (
          <>
            {props.preResourcesInfo && (
              <DialogContentText>{props.preResourcesInfo}</DialogContentText>
            )}
            {props?.resources && <ResourceList resources={props.resources} />}
            {props.info && <DialogContentText>{props.info}</DialogContentText>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {props.actions ? (
          props.actions
        ) : (
          <>
            <Button
              loading={props.loading}
              disabled={props.disableConfirm}
              onClick={handleConfirm}
              data-testid="confirm"
              startIcon={props.confirmStartIcon}
            >
              {props.loading ? "Loading..." : (props.confirmText ?? "Yes, Delete")}
            </Button>
            <Button disabled={props.loading} onClick={handleCancel}>
              {props.cancelText ?? "Cancel"}
            </Button>
          </>
        )}
      </DialogActions>
    </SafeDialog>
  );
}
